import type { JSX } from '@builder.io/qwik';
import { $, component$, useSignal, useVisibleTask$ } from '@builder.io/qwik';
import type {
  FeedVideoPlaylistBlockInterface,
  PlaylistItem,
} from '~/types/AppData/PageBuilder/FeedVideoPlaylistInterface';
import VideoPlayerEmbed from '~/components/Shared/Video/VideoPlayerEmbed';
import { ShowMoreButton } from '~/components/Shared/Buttons/ShowMoreButton';
import { PLAYLIST_CONFIG, PLAYLIST_STYLES } from '~/constants/PLAYLIST';
import { formatTimestampForDisplay } from '~/utilities/formatTimestampForDisplay';
import { formatDuration } from '~/utilities/formatVideoDurationForDisplay';
import PlayIcon from '~/icons/playIcon';
import { cva } from 'class-variance-authority';

declare global {
  interface Window {
    voltax?: any;
  }
}

type FeedVideoPlaylistItemProps = PlaylistItem & {
  index: number;
  currentVideoIndex: number;
};

const feedVideoPlaylistitemImageClasses = cva('aspect-video object-cover', {
  variants: {
    isNowPlaying: {
      true: 'bg-black opacity-50',
      false: 'bg-red-500',
    },
  },
});

const upNextTitleClasses = cva('font-group-big font-bold', {
  variants: {
    isUpNext: {
      true: 'line-clamp-2',
      false: 'line-clamp-3',
    },
  },
});

const shouldShowToggleButton = (
  currentItemsShown: number,
  totalPlaylistItems: number
): boolean => {
  const hasMinimumItems =
    totalPlaylistItems > PLAYLIST_CONFIG.MIN_ITEMS_FOR_SHOW_MORE;
  const canShowMore = currentItemsShown < PLAYLIST_CONFIG.MAX_ITEMS;
  const hasMoreToShow = currentItemsShown < totalPlaylistItems;

  return hasMinimumItems && canShowMore && hasMoreToShow;
};

const FeedVideoPlaylistItem = component$<FeedVideoPlaylistItemProps>(
  ({
    title,
    image,
    duration,
    create_date,
    payload_id,
    index,
    currentVideoIndex,
  }): JSX.Element => {
    const formattedDate = formatTimestampForDisplay(create_date);
    const formattedDuration = formatDuration(duration);
    const isNowPlaying = index === currentVideoIndex;
    const isUpNext = index === currentVideoIndex + 1;

    return (
      <div
        class='flex mb-4 gap-4 cursor-pointer'
        data-video-item
        data-video-payload_id={payload_id}
        data-video-index={index}
      >
        <div>
          <div class='size-fit relative'>
            <img
              src={image}
              alt={title}
              width={PLAYLIST_STYLES.IMAGE_WIDTH}
              height={PLAYLIST_STYLES.IMAGE_HEIGHT}
              class={feedVideoPlaylistitemImageClasses({ isNowPlaying })}
            />
            <div class='absolute inset-0 flex items-center justify-center'>
              {isNowPlaying ? (
                <div class='font-group-big font-bold'>NOW PLAYING</div>
              ) : (
                <div class='w-10 h-10'>
                  <PlayIcon svgClass='w-full h-full' />
                </div>
              )}
            </div>
          </div>
        </div>
        <div class='flex flex-col gap-2 w-3/4 h-full'>
          {isUpNext && (
            <div class='text-secondary font-group-tiny underline decoration-primary decoration-2'>
              UP NEXT
            </div>
          )}
          <div class={upNextTitleClasses({ isUpNext })}>{title}</div>
          <div class='text-secondary font-group-tiny'>{`${formattedDuration} | ${formattedDate}`}</div>
        </div>
      </div>
    );
  }
);

export default component$<FeedVideoPlaylistBlockInterface['props']>(
  ({ metadata, oEmbed, title, playlist }): JSX.Element => {
    const currentVideoIndex = useSignal<number>(0);
    const currentVideo = useSignal<PlaylistItem>(playlist[0]);
    const mobileItemsToShow = useSignal<number>(
      PLAYLIST_CONFIG.MIN_ITEMS_FOR_SHOW_MORE
    );
    const showLessMode = useSignal<boolean>(false);

    useVisibleTask$(({ track }) => {
      let vplayer: any;

      // Track changes to mobileItemsToShow so we can reattach listeners when items are added
      track(() => mobileItemsToShow.value);

      const addEvents = (player: any) => {
        player.on('videoComplete', (params: any) => {
          const nextIndex =
            params.index + 1 < playlist.length ? params.index + 1 : 0;
          currentVideo.value = playlist[nextIndex];
          currentVideoIndex.value = nextIndex;
        });
      };

      const obtainPlayer = () => {
        const vplayers = window.voltax.getAllPlayersOnPage();
        vplayer = vplayers[0];
        addEvents(vplayer);
      };

      if (window.voltax) {
        obtainPlayer();
      } else {
        window.addEventListener('voltaxPlayerLoaded', () => {
          obtainPlayer();
        });
      }

      // Add click event listeners for all video items
      const handleVideoItemClick = (event: Event) => {
        const target = event.currentTarget as HTMLElement;
        const payloadId = target.getAttribute('data-video-payload_id');
        const videoIndex = target.getAttribute('data-video-index') || '0';

        if (vplayer) {
          vplayer.setContent(payloadId, videoIndex);
          currentVideo.value = playlist[Number(videoIndex)];
          currentVideoIndex.value = Number(videoIndex);
        }
      };

      // Remove existing listeners first to avoid duplicates
      const existingItems = document.querySelectorAll('[data-video-item]');
      existingItems.forEach((item) => {
        item.removeEventListener('click', handleVideoItemClick);
      });

      const videoItems = document.querySelectorAll('[data-video-item]');
      videoItems.forEach((item) => {
        item.addEventListener('click', handleVideoItemClick);
      });

      // Cleanup function to remove event listeners
      return () => {
        videoItems.forEach((item) => {
          item.removeEventListener('click', handleVideoItemClick);
        });
      };
    });

    if (!metadata?.contentId) {
      return null;
    }

    const currentVideoCreateDate = formatTimestampForDisplay(
      currentVideo?.value?.create_date
    );
    const currentVideoDuration = formatDuration(currentVideo?.value?.duration);

    const playlistItems = playlist.slice(0, PLAYLIST_CONFIG.MAX_ITEMS);
    const mobileItems = playlistItems.slice(0, mobileItemsToShow.value);
    const shouldShowMoreButton = shouldShowToggleButton(
      mobileItemsToShow.value,
      playlist.length
    );

    // Handle mobile show more/less button
    const handleMobileToggle = $(() => {
      if (showLessMode.value) {
        // Show less - collapse back to initial state
        mobileItemsToShow.value = PLAYLIST_CONFIG.MIN_ITEMS_FOR_SHOW_MORE;
        showLessMode.value = false;
      } else {
        // Show more
        const newCount = Math.min(
          mobileItemsToShow.value + PLAYLIST_CONFIG.ITEMS_PER_LOAD,
          PLAYLIST_CONFIG.MAX_ITEMS,
          playlist.length
        );
        mobileItemsToShow.value = newCount;
        // Check if we've reached the end
        if (newCount >= Math.min(PLAYLIST_CONFIG.MAX_ITEMS, playlist.length)) {
          showLessMode.value = true;
        }
      }
    });

    return (
      <div class='block lg:px-[20px] bg-[#121212] text-white py-[30px]'>
        <div class={`max-w-[1060px] lg:mx-auto`}>
          <div class='mx-[20px] lg:mx-0'>
            <div class='font-group-large'>{title}</div>
            <div class='w-full h-full border border-white my-[15px]'></div>
          </div>
          <div class='block lg:flex gap-4 flex-2'>
            <div class='w-full mb-5 lg:mb-0'>
              <div class='left-0 w-full mb-[10px]'>
                {oEmbed?.html ? (
                  <VideoPlayerEmbed
                    html={oEmbed.html}
                    version={oEmbed.version}
                  />
                ) : null}
              </div>
              <div class='px-[20px] lg:px-0'>
                <div class='lg:line-clamp-2 font-industry font-group-huge '>
                  {currentVideo?.value?.title}
                </div>
                <div class='lg:line-clamp-2 font-group-big'>
                  {currentVideo?.value?.description}
                </div>
                <div class='text-secondary font-group-tiny '>{`${currentVideoDuration} | ${currentVideoCreateDate}`}</div>
              </div>
            </div>
            <div class='px-[20px] lg:px-0'>
              {/* Desktop: Show all items with scroll */}
              <div
                class={`hidden lg:block max-h-[450px] overflow-y-auto custom-scrollbar`}
              >
                {playlistItems.map((item, index) => (
                  <FeedVideoPlaylistItem
                    key={item.payload_id}
                    {...item}
                    index={index}
                    currentVideoIndex={currentVideoIndex.value}
                  />
                ))}
              </div>

              {/* Mobile & Tablet: Show limited items with "Show More" button */}
              <div class='block lg:hidden'>
                {mobileItems.map((item, index) => (
                  <FeedVideoPlaylistItem
                    key={item.payload_id}
                    {...item}
                    index={index}
                    currentVideoIndex={currentVideoIndex.value}
                  />
                ))}

                {(shouldShowMoreButton || showLessMode.value) && (
                  <div class='flex justify-center mt-4 w-full'>
                    <ShowMoreButton
                      showLessMode={showLessMode.value}
                      itemsToShow={mobileItemsToShow.value}
                      onToggle={handleMobileToggle}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);
